import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, Button, Card, DataTable, EmptyState, IconButton, Tabs, Toast, type Column } from '../../design';
import { useAuth } from '../../auth/AuthProvider';
import { CalEmbed } from '../../components/CalEmbed';
import {
  formatDate,
} from '../../lib/clients';
import {
  formatDateTime,
  formatDuration,
  formatPrice,
  formatTime,
  sessionsApi,
  sessionStatusTone,
} from '../../lib/sessions';
import { SESSION_STATUS_LABELS, type SessionItem } from '../../lib/types';

type Bucket = 'upcoming' | 'past';

function bucketOf(s: SessionItem, now: number): Bucket {
  // Cancelled sessions stay in their time bucket (muted + badge) so the viewer
  // sees the slot was freed; they age into Past naturally.
  const end = new Date(s.endsAt ?? s.startsAt).getTime();
  return end >= now ? 'upcoming' : 'past';
}

/** Monday 00:00 local of the week `offset` weeks away from the current one. */
function startOfWeek(offset: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + offset * 7);
  return d;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function SessionsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isClient = user?.role === 'client';

  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [bookingLink, setBookingLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [tab, setTab] = useState<Bucket>('upcoming');
  const [weekOffset, setWeekOffset] = useState(0);

  async function load() {
    try {
      const res = await sessionsApi.list();
      setSessions(res.sessions);
      setBookingLink(res.bookingLink);
    } catch {
      setError('Could not load sessions.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function onBooked() {
    setNotice('Session booked — the confirmation email is on its way.');
    void load();
    // The webhook may lag the embed's success event; one delayed retry catches
    // the row without polling machinery.
    setTimeout(() => void load(), 2500);
  }

  const buckets = useMemo(() => {
    const now = Date.now();
    const upcoming = sessions.filter((s) => bucketOf(s, now) === 'upcoming');
    const past = sessions.filter((s) => bucketOf(s, now) === 'past');
    upcoming.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    // past already arrives newest-first
    return { upcoming, past };
  }, [sessions]);

  const visible = buckets[tab];

  // Week-at-a-glance for practitioners/admins: booked sessions only, stacked
  // per day. This deliberately renders *bookings*, never availability — the
  // bookable grid is the embed's job (see spec 0007).
  const week = useMemo(() => {
    const start = startOfWeek(weekOffset);
    const days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(start.getTime() + i * 24 * 60 * 60 * 1000);
      const items = sessions
        .filter(
          (s) =>
            s.status !== 'cancelled' && s.status !== 'pending_payment' && sameDay(new Date(s.startsAt), date),
        )
        .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
      return { date, items };
    });
    return { start, days, count: days.reduce((n, d) => n + d.items.length, 0) };
  }, [sessions, weekOffset]);

  const today = new Date();

  const muted = (s: SessionItem) => (s.status === 'cancelled' ? { color: 'var(--text-muted)' } : undefined);

  const sessionColumn: Column<SessionItem> = {
    key: 'title',
    header: 'Session',
    render: (s) => {
      const price = formatPrice(s.amountCents, s.currency);
      return (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, ...muted(s) }}>
          <span style={{ color: s.status === 'cancelled' ? 'var(--text-muted)' : 'var(--text-strong)' }}>
            {s.title ?? 'Session'}
          </span>
          {price && <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{price}</span>}
        </span>
      );
    },
  };

  const columns: Column<SessionItem>[] = [
    ...(isClient
      ? []
      : [
          {
            key: 'client',
            header: 'Client',
            render: (s: SessionItem) => (
              <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', ...muted(s) }}>
                <Avatar name={s.client?.name ?? s.attendeeName ?? '?'} size="sm" />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ color: s.status === 'cancelled' ? 'var(--text-muted)' : 'var(--text-strong)' }}>
                    {s.client?.name ?? s.attendeeName ?? 'Unknown'}
                  </span>
                  {!s.client && (
                    <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>Not a client yet</span>
                  )}
                </span>
              </span>
            ),
          } as Column<SessionItem>,
        ]),
    sessionColumn,
    { key: 'when', header: 'When', render: (s) => <span style={muted(s)}>{formatDateTime(s.startsAt)}</span> },
    { key: 'length', header: 'Length', render: (s) => <span style={muted(s)}>{formatDuration(s.startsAt, s.endsAt)}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (s) => (
        <Badge dot tone={sessionStatusTone(s.status)}>
          {SESSION_STATUS_LABELS[s.status]}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (s) => {
        const actionable = s.status === 'confirmed' && new Date(s.startsAt).getTime() > Date.now();
        if (!actionable) return null;
        return (
          // Row click navigates (practitioner view); keep link clicks out of it.
          <span
            style={{ display: 'inline-flex', gap: 'var(--space-3)', alignItems: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            {s.videoUrl && (
              <Button size="sm" variant="ghost" icon="video" onClick={() => window.open(s.videoUrl!, '_blank', 'noopener')}>
                Join
              </Button>
            )}
            {s.rescheduleUrl && (
              <Button size="sm" variant="ghost" onClick={() => window.open(s.rescheduleUrl!, '_blank', 'noopener')}>
                Reschedule
              </Button>
            )}
            {s.cancelUrl && (
              <Button size="sm" variant="ghost" onClick={() => window.open(s.cancelUrl!, '_blank', 'noopener')}>
                Cancel
              </Button>
            )}
          </span>
        );
      },
    },
  ];

  const emptyCopy =
    tab === 'upcoming'
      ? isClient
        ? 'No upcoming sessions — pick a time above.'
        : 'No upcoming sessions. Book one from a client’s page.'
      : 'No past sessions yet.';

  return (
    <>
      {notice && (
        <Toast tone="success" title="Session booked" onDismiss={() => setNotice(null)}>
          {notice}
        </Toast>
      )}
      {error && (
        <Toast tone="danger" title="Something went wrong" onDismiss={() => setError(null)}>
          {error}
        </Toast>
      )}

      {!isClient && !loading && (
        <Card
          pad="sm"
          title={`Week of ${formatDate(week.start.toISOString())}`}
          action={
            <span style={{ display: 'inline-flex', gap: 'var(--space-3)', alignItems: 'center' }}>
              {weekOffset !== 0 && (
                <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)}>
                  This week
                </Button>
              )}
              <IconButton icon="chevron-left" label="Previous week" size="sm" onClick={() => setWeekOffset(weekOffset - 1)} />
              <IconButton icon="chevron-right" label="Next week" size="sm" onClick={() => setWeekOffset(weekOffset + 1)} />
            </span>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 'var(--space-3)' }}>
            {week.days.map(({ date, items }) => {
              const isToday = sameDay(date, today);
              return (
                <div key={date.toISOString()} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  <span
                    style={{
                      font: 'var(--type-label)',
                      letterSpacing: 'var(--track-label)',
                      textTransform: 'uppercase',
                      textAlign: 'center',
                      color: isToday ? 'var(--text-brand)' : 'var(--text-muted)',
                    }}
                  >
                    {date.toLocaleDateString(undefined, { weekday: 'short' })} {date.getDate()}
                  </span>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--space-2)',
                      background: isToday ? 'var(--surface-brand-soft)' : 'var(--surface-sunken)',
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-2)',
                      minHeight: 96,
                    }}
                  >
                    {items.map((s) => (
                      <div
                        key={s.id}
                        onClick={() => s.client && navigate(`/clients/${s.client.id}`)}
                        title={`${s.title ?? 'Session'} · ${formatTime(s.startsAt)}`}
                        style={{
                          background: 'var(--navy-600)',
                          color: 'var(--white)',
                          borderRadius: 'var(--radius-md)',
                          padding: 'var(--space-2) var(--space-3)',
                          cursor: s.client ? 'pointer' : 'default',
                          overflow: 'hidden',
                        }}
                      >
                        <div style={{ font: 'var(--type-caption)', opacity: 0.8 }}>{formatTime(s.startsAt)}</div>
                        <div style={{ font: 'var(--type-caption)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                          {s.client?.name ?? s.attendeeName ?? 'Unknown'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {week.count === 0 && (
            <div style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', textAlign: 'center', marginTop: 'var(--space-3)' }}>
              No sessions this week.
            </div>
          )}
        </Card>
      )}

      {isClient && (
        <Card title="Book a session" pad={bookingLink ? 'none' : 'md'}>
          {bookingLink ? (
            <CalEmbed
              namespace="sessions"
              calLink={bookingLink}
              prefill={{ name: user?.name, email: user?.email }}
              onBookingSuccessful={onBooked}
            />
          ) : (
            <EmptyState icon="calendar" title="Booking isn't set up yet">
              Your practitioner will enable online booking soon.
            </EmptyState>
          )}
        </Card>
      )}

      <Card
        pad="none"
        title={isClient ? 'Your sessions' : 'Sessions'}
        action={
          <Tabs
            variant="pill"
            items={[
              { value: 'upcoming', label: `Upcoming (${buckets.upcoming.length})` },
              { value: 'past', label: `Past (${buckets.past.length})` },
            ]}
            value={tab}
            onChange={(v) => setTab(v as Bucket)}
          />
        }
      >
        {loading ? (
          <div style={{ padding: 'var(--space-6)', color: 'var(--text-muted)' }}>Loading…</div>
        ) : visible.length === 0 ? (
          <EmptyState icon="calendar" title={tab === 'upcoming' ? 'Nothing coming up' : 'Nothing here yet'}>
            {emptyCopy}
          </EmptyState>
        ) : (
          <DataTable
            rows={visible}
            columns={columns}
            onRowClick={!isClient ? (s) => s.client && navigate(`/clients/${s.client.id}`) : undefined}
          />
        )}
      </Card>
    </>
  );
}
